import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { orderDiscountLabel } from "@/lib/discounts";
import { toast } from "sonner";

export default function AdminOrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [tracking, setTracking] = useState("");
  const [carrier, setCarrier] = useState("usps");
  const [savingTracking, setSavingTracking] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);

  useEffect(() => {
    apiClient.get("/admin/orders").then((res) => {
      const o = res.data.items.find((x) => x.id === id);
      setOrder(o);
      setTracking(o?.tracking_number || "");
      setCarrier(o?.tracking_carrier || "usps");
    });
  }, [id]);

  const updateStatus = async (field, value) => {
    const { data } = await apiClient.put(`/admin/orders/${id}`, { [field]: value });
    setOrder(data);
    toast.success("Order updated");
  };

  const saveTracking = async () => {
    if (!tracking.trim()) return toast.error("Enter a tracking number first");
    setSavingTracking(true);
    try {
      const { data } = await apiClient.put(`/admin/orders/${id}/tracking`, { tracking_number: tracking, carrier });
      setOrder(data.order);
      toast.success(data.email_sent ? "Tracking saved — customer emailed" : "Tracking saved (email could not be sent)");
    } catch {
      toast.error("Failed to save tracking");
    } finally {
      setSavingTracking(false);
    }
  };

  const [emailingReceipt, setEmailingReceipt] = useState(false);

  const emailReceipt = async () => {
    setEmailingReceipt(true);
    try {
      await apiClient.post(`/admin/orders/${id}/email-receipt`);
      toast.success("Receipt copy emailed to your inbox");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not email receipt");
    } finally {
      setEmailingReceipt(false);
    }
  };

  const [pushing, setPushing] = useState(false);

  const pushToShipStation = async () => {
    setPushing(true);
    try {
      const { data } = await apiClient.post(`/admin/orders/${id}/push-shipstation`);
      if (data.pushed) {
        toast.success(data.message);
        setOrder((o) => ({ ...o, shipstation_order_id: data.shipstation_order_id }));
      } else {
        toast.error(data.message);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not push order to ShipStation");
    } finally {
      setPushing(false);
    }
  };

  const markPaid = async () => {
    setMarkingPaid(true);
    try {
      const { data } = await apiClient.post(`/admin/orders/${id}/mark-paid`);
      setOrder(data.order);
      toast.success(data.email_sent ? "Marked as paid — receipt emailed to customer" : "Marked as paid (receipt email could not be sent)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not mark order as paid");
    } finally {
      setMarkingPaid(false);
    }
  };

  if (!order) return <div className="h-64 bg-white rounded-md animate-pulse" data-testid="admin-order-detail-loading" />;

  return (
    <div className="max-w-2xl" data-testid="admin-order-detail-page">
      <Link to="/admin/orders" className="text-sm text-brandblue-600 font-semibold">&larr; Orders</Link>
      <h1 className="text-2xl font-display font-semibold text-slate-900 mt-3 mb-6">{order.order_number}</h1>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 space-y-5">
        {order.payment_status !== "paid" ? (
          <div className="flex items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <p className="text-sm text-amber-800 font-medium">Awaiting payment (Zelle)</p>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={markPaid} disabled={markingPaid} data-testid="admin-order-mark-paid-btn">
              {markingPaid ? "Marking..." : "Mark as Paid & Email Receipt"}
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3" data-testid="admin-order-paid-banner">
            <p className="text-sm text-emerald-700 font-semibold">
              Paid{order.paid_at ? ` on ${new Date(order.paid_at).toLocaleString()}` : ""}
            </p>
            <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-100" onClick={emailReceipt} disabled={emailingReceipt} data-testid="admin-order-email-receipt-btn">
              {emailingReceipt ? "Sending..." : "Email Me Receipt"}
            </Button>
          </div>
        )}
        {order.fulfillment_status !== "cancelled" && !order.local_pickup && (
          <div className={`flex items-center justify-between gap-3 rounded-lg px-4 py-3 border ${order.shipstation_order_id ? "bg-slate-50 border-slate-200" : "bg-orange-50 border-orange-200"}`} data-testid="admin-order-shipstation-status">
            {order.shipstation_order_id ? (
              <p className="text-sm text-slate-600">In ShipStation <span className="font-semibold text-slate-900">(id {order.shipstation_order_id})</span></p>
            ) : (
              <>
                <p className="text-sm text-orange-800 font-medium">Not in ShipStation — push failed or was skipped</p>
                <Button size="sm" variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-100" onClick={pushToShipStation} disabled={pushing} data-testid="admin-order-push-shipstation-btn">
                  {pushing ? "Pushing..." : "Push to ShipStation"}
                </Button>
              </>
            )}
          </div>
        )}
        {order.confirmation_email_sent != null && (
          <p className="text-xs text-slate-500" data-testid="admin-order-confirmation-email-status">
            Order confirmation email: {order.confirmation_email_sent ? <span className="text-emerald-600 font-semibold">sent to customer</span> : <span className="text-red-600 font-semibold">FAILED to send</span>}
          </p>
        )}
        {order.payment_last4 && (
          <p className="text-sm text-slate-600" data-testid="admin-order-payment-card">
            Payment method: <span className="font-semibold text-slate-900">{order.payment_brand || "Card"} •••• {order.payment_last4}</span>
          </p>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Payment Status</Label>
            <Select value={order.payment_status} onValueChange={(v) => updateStatus("payment_status", v)}>
              <SelectTrigger data-testid="admin-order-payment-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Fulfillment Status</Label>
            <Select value={order.fulfillment_status} onValueChange={(v) => updateStatus("fulfillment_status", v)}>
              <SelectTrigger data-testid="admin-order-fulfillment-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="shipped">Shipped</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-3">
          {order.shipping_carrier && (
            <p className="text-xs text-slate-500" data-testid="admin-order-shipping-method">
              Customer selected: <span className="font-semibold text-slate-900">{order.shipping_carrier} {order.shipping_service}</span> (${order.shipping_cost.toFixed(2)})
            </p>
          )}
          <div className="flex gap-2 items-end">
            <div className="w-36">
              <Label>Carrier</Label>
              <Select value={carrier} onValueChange={setCarrier}>
                <SelectTrigger data-testid="admin-order-carrier-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="usps">USPS</SelectItem>
                  <SelectItem value="ups">UPS</SelectItem>
                  <SelectItem value="fedex">FedEx</SelectItem>
                  <SelectItem value="dhl_express">DHL Express</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1"><Label>Tracking Number</Label><Input value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid="admin-order-tracking-input" /></div>
            <Button size="sm" onClick={saveTracking} disabled={savingTracking} data-testid="admin-order-tracking-save">
              {savingTracking ? "Saving..." : "Save & Email Customer"}
            </Button>
          </div>
          <p className="text-[11px] text-slate-400">Saving marks the order as shipped and emails the tracking number to the customer.</p>
        </div>

        <div className="pt-4 border-t border-slate-100 space-y-2">
          {order.items.map((item) => (
            <div key={item.product_id} className="flex justify-between text-sm text-slate-600">
              <span>{item.name} ({item.strength}) x{item.quantity}</span><span>${(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
        </div>

        <div className="pt-4 border-t border-slate-100 space-y-1.5 text-sm text-slate-600">
          <div className="flex justify-between"><span>Subtotal</span><span>${order.subtotal.toFixed(2)}</span></div>
          {order.discount > 0 && (
            <div className="flex justify-between text-emerald-600" data-testid="admin-order-discount">
              <span>{orderDiscountLabel(order)}</span>
              <span>-${order.discount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between"><span>Shipping</span><span>${order.shipping_cost.toFixed(2)}</span></div>
          {order.tax_amount > 0 && <div className="flex justify-between"><span>Sales Tax ({order.tax_rate.toFixed(0)}%)</span><span data-testid="admin-order-tax">${order.tax_amount.toFixed(2)}</span></div>}
        </div>
        <div className="pt-4 border-t border-slate-100 flex justify-between font-semibold text-slate-900">
          <span>Total</span><span data-testid="admin-order-total">${order.total.toFixed(2)}</span>
        </div>

        <div className="pt-4 border-t border-slate-100 text-sm text-slate-600">
          <p className="font-semibold text-slate-900 mb-1">Shipping Address</p>
          <p>{order.shipping_address.full_name} — {order.shipping_address.email}</p>
          {order.shipping_address.company && <p data-testid="admin-order-company">Company / Institution: {order.shipping_address.company}</p>}
          <p>{order.shipping_address.line1}, {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.postal_code}, {order.shipping_address.country}</p>
        </div>

        <div className="pt-4 border-t border-slate-100 text-sm text-slate-600 grid sm:grid-cols-2 gap-2">
          <p className="font-semibold text-slate-900 sm:col-span-2 mb-1">Research Use Only Acknowledgment</p>
          <div className="flex justify-between"><span>Accepted</span><span className="font-semibold text-emerald-600" data-testid="admin-order-ruo-accepted">{order.ruo_acknowledged ? "Yes" : "No"}</span></div>
          <div className="flex justify-between"><span>Timestamp</span><span className="font-semibold text-slate-900" data-testid="admin-order-ruo-timestamp">{order.ruo_acknowledged_at ? new Date(order.ruo_acknowledged_at).toLocaleString() : "—"}</span></div>
          <div className="flex justify-between"><span>Terms Version</span><span className="font-semibold text-slate-900" data-testid="admin-order-terms-version">v{order.terms_version}</span></div>
          <div className="flex justify-between"><span>Disclaimer Version</span><span className="font-semibold text-slate-900" data-testid="admin-order-disclaimer-version">v{order.ruo_disclaimer_version}</span></div>
        </div>
      </div>
    </div>
  );
}
